import React, { useState } from 'react';
import { VariablesOf } from '@guuru/graphql-common/gql';
import { Form, useForm } from '@guuru/react-forms';
import { useMutation } from '@apollo/client';
import { error, success } from '@guuru/react-message';
import { useMomentPictureSubmit } from '@guuru/react-upload-hooks';
import { RcFile, UploadFile } from 'antd/es/upload';
import About from './About';
import Moments from './Moments';
import useCurrentUser from '../../../../../../hooks/useCurrentUser';
import UPDATE_COMMUNITY_PROFILE from './mutations/updateCommunityProfile';

type MomentInput = VariablesOf<typeof UPDATE_COMMUNITY_PROFILE>['moments']['momentsToCreate'][0];

interface Moment {
  url: string;
  id: string;
}

interface FormFields {
  moments: Moment[] | MomentInput[];
  bio: string;
}

interface DetailsFormProps {
  partnerId: string;
  name: string;
  moments: Moment[];
  bio: string | null;
}

const filterMomentsUrls = (
  oldMoments: Moment[] | MomentInput[],
  newMoments: Moment[] | MomentInput[],
  shouldKeep: boolean = false,
): MomentInput[] => {
  if (!newMoments.length) {
    return shouldKeep ? [] : oldMoments.map(({ url }) => ({ url }));
  }

  return oldMoments.reduce((urlsList: MomentInput[], moment) => {
    const momentExists = newMoments.find(({ url }) => moment.url === url);
    if (shouldKeep ? momentExists : !momentExists) {
      urlsList.push({ url: moment.url });
    }
    return urlsList;
  }, []);
};

const getNewMomentsUrls = (
  newMoments: MomentInput[] | UploadFile[],
  // eslint-disable-next-line no-unused-vars
  uploadFn: {(file?: RcFile): Promise<MomentInput>},
): Promise<MomentInput[]> => {
  if (!newMoments.length) return Promise.resolve([]);

  const uploadPromises = newMoments
    .filter((file): file is UploadFile => 'originFileObj' in file)
    .map((file) => uploadFn(file.originFileObj));

  return Promise.all(uploadPromises);
};

const CommunityProfile = function ({
  partnerId,
  bio,
  name,
  moments,
}: DetailsFormProps) {
  const { id: expertId } = useCurrentUser()!;
  const [loading, setLoading] = useState(false);
  const [updateCommunityProfile] = useMutation(UPDATE_COMMUNITY_PROFILE);
  const { uploadMomentPicture } = useMomentPictureSubmit({
    expertId,
    partnerId,
  });
  const [form] = useForm();

  const onFinish = async (fields: FormFields) => {
    setLoading(true);
    const momentsToCreate = await getNewMomentsUrls(
      fields.moments,
      uploadMomentPicture,
    );

    try {
      await updateCommunityProfile({
        variables: {
          expertId,
          partnerId,
          bio: fields.bio,
          moments: {
            momentsToCreate,
            momentsToRemove: filterMomentsUrls(moments, fields.moments, false),
          },
        },
      });
    } catch (e: any) {
      error(e.message);
      form.resetFields();
      setLoading(false);
      return Promise.resolve();
    }

    if (momentsToCreate.length) {
      const momentsToKeep = filterMomentsUrls(moments, fields.moments, true);
      momentsToCreate.forEach(({ url }) => momentsToKeep.push({ url }));
      form.setFieldsValue({ moments: momentsToKeep });
    }

    success('Community details updated');

    setLoading(false);

    return Promise.resolve();
  };

  return (
    <Form
      form={form}
      onSave={onFinish}
      savingMessage="Updating community details..."
      initialValues={{ bio, moments }}
      isSaving={loading}
    >
      <About partnerId={partnerId} name={name} bio={bio} loading={loading} />
      <Moments loading={loading} />
    </Form>
  );
};

export default CommunityProfile;
